import styled from '@emotion/styled'
import {
  ErpExtranetShopOrderHistoryQuerySortKeys,
  ExtranetOrderHistoryListItemDto,
  GetExtranetShopOrderQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  useBreakpoint,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacH4, PharmacIcon } from '@pharmac/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import React, { ReactNode, useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { ExtranetWrapper } from '../../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../../components/filters/ExtranetFilters'
import { NoResultsResponse } from '../../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../../features/infinite-query/util/cleanQueryParams'
import { getPages } from '../../../features/infinite-query/util/getPages'
import { api } from '../../../services/HttpService'
import { ShopOrderHistoryFilters } from './ShopOrderHistoryFilters'
import { ShopOrderHistoryRow } from './ShopOrderHistoryRow'
import { mapShopOrderParamsToQueryParams } from './util/query-parameter-mapping'

const SortColumnArrow = styled.div<{ isAsc: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;

  margin-left: var(--spacing-small);

  svg {
    transition: var(--transition-cubic-bezier);
    transform: rotate(${(p) => (p.isAsc ? '180deg' : '0deg')});
  }
`
const SortColumnInner = styled.div`
  display: flex;
  flex-direction: row;
`

export type GetExtranetShopOrderParams = Omit<GetExtranetShopOrderQueryDto, 'pageSize'>

export const ShopOrderHistory = () => {
  const { customer } = useCustomerContext()
  const { minDesktop, maxMobile } = useBreakpoint()
  const { t } = useLanguage(['extranet', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()

  const [params, setParams] = useState<GetExtranetShopOrderParams>(
    mapShopOrderParamsToQueryParams(searchParams)
  )

  useEffect(() => {
    setParams(() => mapShopOrderParamsToQueryParams(searchParams))
  }, [searchParams])

  const activeFilterCount = () => {
    let filterCount = Object.keys(params).length - 1
    if (params.status) {
      filterCount += params.status.length
    }
    if (Object.keys(params).includes('sortOrder')) return filterCount - 1

    return filterCount
  }

  const hasActiveFilters = () => {
    let filterCount = Object.keys(params).length - 1
    if (params.status) {
      filterCount += params.status.length
    }
    const hasNoParams = filterCount === 0

    return !hasNoParams
  }

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  const handleReset = () => {
    setSearchParams(createSearchParams({}))
  }

  const handleSorting = (key: ErpExtranetShopOrderHistoryQuerySortKeys) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          sortKey: key,
          sortOrder: key !== params.sortKey ? 'asc' : params.sortOrder === 'asc' ? 'desc' : 'asc',
        })
      )
    )
  }

  const fetchPage = (pageParam: number) => {
    return api.shop.getShopOrderHistory(customer!.id, {
      pageSize: 40,
      ...params,
      page: pageParam,
    })
  }

  const {
    data: shopOrders,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      '__shopOrderHistory',
      customer?.id,
      params.buyer,
      params.orderCreatedAt,
      params.orderNumber,
      params.page,
      params.sortKey,
      params.sortOrder,
      params.status,
    ],

    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const [filteredShopOrders, setFilteredShopOrders] = useState<ExtranetOrderHistoryListItemDto[]>(
    []
  )

  useEffect(() => {
    setFilteredShopOrders(() => {
      let order = [...getPages(shopOrders)]
      return order
    })
  }, [shopOrders, isLoading, params])

  const noResults = !isLoading && !isFetchingNextPage && filteredShopOrders.length === 0

  const SortColumn = (props: {
    sortKey: ErpExtranetShopOrderHistoryQuerySortKeys
    children: ReactNode
  }) => {
    const order = params.sortOrder ?? 'asc'
    const isActive = params.sortKey === props.sortKey

    return (
      <SortColumnInner>
        <div>{props.children}</div>
        {isActive && (
          <SortColumnArrow
            isAsc={order === 'asc'}
            id="ARROR CONTTI"
          >
            <PharmacIcon icon="chevronDown" />
          </SortColumnArrow>
        )}
        {!isActive && !params.sortKey && (
          <SortColumnArrow isAsc={false}>
            <PharmacIcon icon="chevronDown" />
          </SortColumnArrow>
        )}
      </SortColumnInner>
    )
  }

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        paddingSmallHorizontal={!minDesktop}
        paddingMediumBottom
      >
        <ExtranetContainer
          full
          alignCenter={minDesktop}
          paddingLargeVertical={minDesktop}
          paddingLargeTop={!minDesktop}
          paddingMediumBottom={!minDesktop}
        >
          <PharmacH4
            style={{ color: 'var(--color-primary)' }}
            paddingMediumRight
          >
            {t('shop.orders.shopOrders')}
          </PharmacH4>
          {!minDesktop && (
            <>
              <div style={{ marginLeft: 'var(--spacing-large)' }} />
              <ExtranetFilters
                activeFilterCount={activeFilterCount()}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={hasActiveFilters()}
                render={({ closeFilters }) => (
                  <ShopOrderHistoryFilters closeFilters={closeFilters} />
                )}
              />
            </>
          )}
        </ExtranetContainer>
        {minDesktop && (
          <>
            <ExtranetContainer
              half
              paddingLargeVertical={minDesktop}
              id="filters"
              justifyEnd
            >
              <ExtranetFilters
                activeFilterCount={activeFilterCount()}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={hasActiveFilters()}
                render={({ closeFilters }) => (
                  <ShopOrderHistoryFilters closeFilters={closeFilters} />
                )}
              />
            </ExtranetContainer>
          </>
        )}
      </ExtranetContainer>

      <ExtranetContainer
        full
        paddingSmallHorizontal={!minDesktop}
        flexRow
      >
        <ExtranetTable
          style={{
            fontFamily: 'var(--font-family-secondary)',
            fontWeight: 300,
          }}
        >
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--extranet-light-purple)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            <ExtranetTableColumn
              size={maxMobile ? 1 : 2}
              style={{ overflowWrap: 'anywhere' }}
              onClick={() => handleSorting(ErpExtranetShopOrderHistoryQuerySortKeys.Id)}
            >
              <SortColumn sortKey={ErpExtranetShopOrderHistoryQuerySortKeys.Id}>
                {t('shop.orders.orderNumber')}
              </SortColumn>
            </ExtranetTableColumn>

            <ExtranetTableColumn
              size={maxMobile ? 1 : 2}
              style={{ overflowWrap: 'anywhere' }}
              onClick={() => handleSorting(ErpExtranetShopOrderHistoryQuerySortKeys.OrderCreatedAt)}
            >
              <SortColumn sortKey={ErpExtranetShopOrderHistoryQuerySortKeys.OrderCreatedAt}>
                {t('shop.orders.orderDate')}
              </SortColumn>
            </ExtranetTableColumn>
            {!maxMobile && (
              <>
                <ExtranetTableColumn
                  size={2}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {t('shop.orders.orderStatus')}
                </ExtranetTableColumn>
                <ExtranetTableColumn
                  size={2}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {t('shop.orders.totalPrice')}
                </ExtranetTableColumn>
                <ExtranetTableColumn
                  size={2}
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {t('shop.orders.purchaserName')}
                </ExtranetTableColumn>
              </>
            )}
            <ExtranetExpandArrowColumn
              size={1}
              style={{ overflowWrap: 'anywhere' }}
            />
          </ExtranetTableRow>

          <React.Fragment>
            {!noResults &&
              filteredShopOrders.map((order) => (
                <ShopOrderHistoryRow
                  key={order.orderNumber}
                  order={order}
                />
              ))}
            {noResults && <NoResultsResponse />}
          </React.Fragment>
        </ExtranetTable>
      </ExtranetContainer>
      <div
        ref={scrollRef}
        style={{ height: 20 }}
      />
    </ExtranetWrapper>
  )
}
